import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  CssBaseline,
  FormControlLabel,
  GlobalStyles,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/Schedule'
import CheckIcon from '@mui/icons-material/Check'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import PauseRoundedIcon from '@mui/icons-material/PauseRounded'
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { createAffexyTheme } from '@/theme/affexyTheme'
import { affexyColors as C } from '@/theme/affexyTokens'
import { loadFaceModels, detectFaces } from '@/lib/faceApi'
import { youTubeId } from '@/utils/youtube'
import { isMobileDevice } from '@/utils/device'
import { fmtTime } from '@/components/researcher/liveDemo/constants'
import SwappableStage from '@/components/researcher/emotions/SwappableStage'
import { stimulusDrivesClock } from '@/components/researcher/emotions/stimulusVideo'
import EmotionTimeline from '@/components/researcher/emotions/EmotionTimeline'
import EmotionSummaryPanel, { PL_LABEL } from '@/components/respondent/EmotionSummaryPanel'
import EyeTrackingCalibration, { type CalibrationPoint } from '@/components/respondent/EyeTrackingCalibration'
import FigmaTaskStimulus from '@/components/respondent/FigmaTaskStimulus'
import { exitFullscreenAndUnlock } from '@/utils/fullscreen'
import { RESPONDENT_KEYFRAMES } from '@/components/respondent/keyframes'
import { ApiError } from '@/services/baseService'
import {
  respondentService,
  type RespondentStudy,
  type RespondentQuestion,
  type RespondentAnswer,
  type RespondentResults,
  type RespondentStimulusResult,
  type PrototypeSession,
} from '@/services/respondentService'

type Step =
  | 'loading'
  | 'error'
  | 'landing'
  | 'consent'
  | 'tech'
  | 'calibration'
  | 'stimulus'
  | 'questions'
  | 'uploading'
  | 'thanks'

/**
 * Lifecycle of the face-recording upload. It starts in the background the
 * moment the stimulus ends (while the respondent answers questions) and the
 * 'uploading' screen surfaces its progress only if it is still in flight when
 * the journey wraps up.
 */
type UploadState =
  | { phase: 'idle' }
  | { phase: 'uploading'; sent: number; total: number }
  | { phase: 'done' }
  | { phase: 'error' }

/** A stimulus's on-screen window relative to the recording start (ms). */
type StimulusMarker = { stimulus_id?: number; order?: number; start_ms: number; end_ms: number }

const TOTAL_STEPS = 6

/** Grab a small face still from the live webcam stream, to use as the
 *  respondent's avatar in the researcher UI. Mirrored to match the selfie
 *  preview the respondent just saw. Resolves null on any failure — the avatar
 *  is a nice-to-have and must never block the recording. */
async function captureThumbnail(stream: MediaStream): Promise<Blob | null> {
  try {
    const video = document.createElement('video')
    video.muted = true
    video.playsInline = true
    video.srcObject = stream
    await video.play().catch(() => {})
    // Wait for a decoded frame (metadata alone can still be a blank buffer).
    await new Promise<void>((resolve) => {
      if (video.readyState >= 2 && video.videoWidth) return resolve()
      const done = () => resolve()
      video.onloadeddata = done
      window.setTimeout(done, 600)
    })
    const vw = video.videoWidth || 320
    const vh = video.videoHeight || 240
    if (!vw || !vh) return null
    // Cover-crop to a compact square avatar.
    const size = 160
    const scale = Math.max(size / vw, size / vh)
    const dw = vw * scale
    const dh = vh * scale
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.translate(size, 0)
    ctx.scale(-1, 1) // mirror
    ctx.drawImage(video, (size - dw) / 2, (size - dh) / 2, dw, dh)
    video.srcObject = null
    return await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.72),
    )
  } catch {
    return null
  }
}

/** Choose a MediaRecorder mime the browser actually supports. */
function pickRecorderMime(): string | undefined {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
    'video/mp4',
  ]
  if (typeof MediaRecorder === 'undefined') return undefined
  return candidates.find((m) => MediaRecorder.isTypeSupported(m))
}

/** Whether the study presents any sound-bearing stimulus (video or URL/YouTube). */
function studyHasAudio(study: RespondentStudy): boolean {
  return study.stimuli.some((s) => (s.type === 'video' || s.type === 'url') && !!s.url)
}

// ───────────────────────────── small UI atoms ──────────────────────────────

function Stepper({ step, dark = false }: { step: number; dark?: boolean }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: '100%', maxWidth: 420 }}>
      <Typography
        sx={{
          fontSize: 11,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          fontWeight: 700,
          whiteSpace: 'nowrap',
          color: dark ? 'rgba(255,255,255,.55)' : C.ink48,
        }}
      >
        Krok {step + 1} z {TOTAL_STEPS}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ flex: 1 }}>
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <Box
            key={i}
            sx={{
              height: 3,
              flex: 1,
              borderRadius: 34,
              bgcolor:
                i < step
                  ? dark
                    ? '#fff'
                    : C.navy
                  : i === step
                    ? C.coral
                    : dark
                      ? 'rgba(255,255,255,.18)'
                      : C.ink08,
            }}
          />
        ))}
      </Stack>
    </Stack>
  )
}

function Lockup({ dark = false }: { dark?: boolean }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        component="img"
        src={`${import.meta.env.BASE_URL}sygnet.png`}
        alt=""
        sx={{ width: 22, height: 22, objectFit: 'contain', display: 'block' }}
      />
      <Typography sx={{ fontFamily: '"Roboto"', fontWeight: 700, fontSize: 16, color: dark ? '#fff' : C.ink }}>
        FaceWise
      </Typography>
    </Stack>
  )
}

function LightShell({ step, children }: { step: number; children: React.ReactNode }) {
  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: '#fff', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          px: { xs: 2.5, md: 7 },
          py: 1.75,
          borderBottom: `1px solid ${C.ink08}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 3,
        }}
      >
        <Lockup />
        <Box sx={{ display: { xs: 'none', sm: 'block' }, flex: 1, maxWidth: 420 }}>
          <Stepper step={step} />
        </Box>
      </Box>
      <Box sx={{ display: { xs: 'block', sm: 'none' }, px: 2.5, pt: 1.5 }}>
        <Stepper step={step} />
      </Box>
      {children}
    </Box>
  )
}

/** Turns away a phone/tablet before consent, for a study whose task needs a
 *  desktop (a clickable prototype, or eye-tracking calibration). Shows the
 *  current link as plain, copyable text so the respondent can hand it off to
 *  a laptop or desktop instead of typing it from memory. */
function DesktopOnlyScreen({ body }: { body: string }) {
  const shareLink = window.location.href
  const [copied, setCopied] = useState(false)

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      /* clipboard unavailable — the link is still visible to copy by hand */
    }
  }

  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', bgcolor: '#fff', p: 3 }}>
      <Stack spacing={1.5} alignItems="center" sx={{ textAlign: 'center', maxWidth: 420, width: '100%' }}>
        <Lockup />
        <Typography variant="h5" sx={{ mt: 2 }}>
          Otwórz to badanie na komputerze
        </Typography>
        <Typography sx={{ color: C.ink56 }}>{body}</Typography>
        <Stack direction="row" spacing={1} sx={{ width: '100%', mt: 1 }}>
          <TextField
            value={shareLink}
            fullWidth
            size="small"
            inputProps={{ readOnly: true, 'aria-label': 'Link do badania' }}
            onFocus={(e) => e.target.select()}
          />
          <Button
            variant="contained"
            onClick={copyLink}
            startIcon={copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {copied ? 'Skopiowano' : 'Kopiuj link'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}

// ───────────────────────────── main component ──────────────────────────────

export default function RespondentJourney() {
  const theme = useMemo(() => createAffexyTheme(), [])
  const { token = '' } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite') ?? ''

  const [step, setStep] = useState<Step>('loading')
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [study, setStudy] = useState<RespondentStudy | null>(null)
  const [sessionToken, setSessionToken] = useState<string>('')

  // camera + recording
  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const recordStartRef = useRef<number>(0)
  // Face still grabbed at record start; uploaded as the respondent's avatar.
  const thumbnailRef = useRef<Blob | null>(null)
  // Upload progress (drives the 'uploading' screen) + the blob kept for retry.
  const [upload, setUpload] = useState<UploadState>({ phase: 'idle' })
  const pendingUploadRef = useRef<{ blob: Blob; durationSeconds: number } | null>(null)
  // Per-stimulus windows (ms relative to recording start), captured during the
  // stimulus screen so emotions can later be sliced per stimulus.
  const markersRef = useRef<StimulusMarker[]>([])
  // Figma prototype tasks (Z-98): the embed address per stimulus, given with
  // the session, and what the respondent did inside each one.
  const prototypeEmbedsRef = useRef<Record<string, string>>({})
  const prototypeSessionsRef = useRef<PrototypeSession[]>([])
  // Gaze-calibration windows logged on the calibration screen (eye-tracking
  // studies), uploaded with the recording. Empty when skipped — analysis then
  // runs with raw, uncalibrated gaze.
  const calibrationRef = useRef<CalibrationPoint[]>([])
  // Real screen/viewport geometry measured at recording start (Z-45/Z-42) —
  // never a hard-coded guess; feeds Recording.screen_geometry for every
  // recording, not only eye-tracking ones.
  const screenGeometryRef = useRef<{
    screen_w_px: number
    screen_h_px: number
    viewport_w_px: number
    viewport_h_px: number
    device_pixel_ratio: number
  } | null>(null)

  // answers
  const [answers, setAnswers] = useState<Record<number, { choices: number[]; text_answer: string }>>({})

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
  }, [])

  // Fullscreen + orientation lock are only wanted during calibration and the
  // stimulus (entered from the calibration gesture); release them the moment we
  // leave, and on unmount.
  useEffect(() => {
    if (step !== 'calibration' && step !== 'stimulus') exitFullscreenAndUnlock()
  }, [step])
  useEffect(() => () => exitFullscreenAndUnlock(), [])

  // Order of screens depends on when the researcher wants the questions asked.
  // The webcam is always recorded during the stimulus, regardless of timing.
  // Eye-tracking studies add a gaze-calibration screen between the camera check
  // and the stimulus (recording already running, so the dots are captured). It
  // shows no stepper, so the visible step count stays at 6 (see stepIdx below).
  const timing: 'start' | 'end' = study?.question_timing === 'start' ? 'start' : 'end'
  const eyeTracking = !!study?.eye_tracking_enabled
  const sequence = useMemo<Step[]>(() => {
    const steps: Step[] =
      timing === 'start'
        ? ['landing', 'consent', 'questions', 'tech', 'stimulus', 'thanks']
        : ['landing', 'consent', 'tech', 'stimulus', 'questions', 'thanks']
    if (eyeTracking) steps.splice(steps.indexOf('stimulus'), 0, 'calibration')
    return steps
  }, [timing, eyeTracking])
  const nextStep = useCallback((s: Step): Step => sequence[sequence.indexOf(s) + 1] ?? 'thanks', [sequence])

  // Load the study.
  useEffect(() => {
    let cancelled = false
    respondentService
      .getStudy(token)
      .then((data) => {
        if (cancelled) return
        setStudy(data)
        setStep('landing')
      })
      .catch((err) => {
        if (cancelled) return
        setErrorMsg(err instanceof Error ? err.message : 'Nie znaleziono badania')
        setStep('error')
      })
    return () => {
      cancelled = true
    }
  }, [token])

  // Stop the camera when leaving the flow.
  useEffect(() => () => stopStream(), [stopStream])

  // ── begin (consent) ──
  const handleConsent = useCallback(
    async (accessCode: string) => {
      const res = await respondentService.begin(token, {
        consent_data: true,
        consent_age: true,
        consent_rodo: true,
        access_code: accessCode,
        ...(inviteToken ? { invite: inviteToken } : {}),
      })
      setSessionToken(res.session_token)
      prototypeEmbedsRef.current = res.prototype_embeds ?? {}
      setStep(nextStep('consent'))
    },
    [token, inviteToken, nextStep],
  )

  // ── recording control ──
  const startRecording = useCallback(() => {
    const stream = streamRef.current
    if (!stream) return
    chunksRef.current = []
    const mimeType = pickRecorderMime()
    // Cap the bitrate: the default 720p encode runs ~8–9 Mbps (~1 MB/s), which
    // makes a 40s clip ~44 MB and slow to upload. Emotion/face analysis needs a
    // clear face, not HD detail, so ~1.2 Mbps video + 64 kbps audio is plenty
    // and yields roughly a 7× smaller file with no loss for the analysis.
    const options: MediaRecorderOptions = {
      videoBitsPerSecond: 1_200_000,
      audioBitsPerSecond: 64_000,
    }
    if (mimeType) options.mimeType = mimeType
    let recorder: MediaRecorder
    try {
      recorder = new MediaRecorder(stream, options)
    } catch {
      recorder = new MediaRecorder(stream)
    }
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
    }
    recorderRef.current = recorder
    recordStartRef.current = Date.now()
    // Real geometry, not a guess (Z-42: a hard-coded screen size previously
    // made angular gaze error lie on any machine but the one it was tuned on).
    screenGeometryRef.current = {
      screen_w_px: window.screen?.width ?? window.innerWidth,
      screen_h_px: window.screen?.height ?? window.innerHeight,
      viewport_w_px: window.innerWidth,
      viewport_h_px: window.innerHeight,
      device_pixel_ratio: window.devicePixelRatio || 1,
    }
    recorder.start(1000)
    // Grab the avatar frame off the live stream (fire-and-forget: it resolves
    // well before the upload, seconds later after the stimulus).
    void captureThumbnail(stream).then((blob) => {
      thumbnailRef.current = blob
    })
  }, [])

  // Send the recording, reporting progress into `upload` state. The blob stays
  // in pendingUploadRef so a failed attempt can be retried from the
  // 'uploading' screen.
  const performUpload = useCallback(
    async (blob: Blob, durationSeconds: number) => {
      setUpload({ phase: 'uploading', sent: 0, total: blob.size })
      try {
        await respondentService.uploadRecording(
          token,
          sessionToken,
          blob,
          {
            durationSeconds,
            markers: markersRef.current,
            eyeTrackingCalibration: calibrationRef.current.length
              ? { points: calibrationRef.current }
              : null,
            screenGeometry: screenGeometryRef.current,
            thumbnail: thumbnailRef.current,
            prototypeSessions: prototypeSessionsRef.current,
          },
          (sent, total) => setUpload({ phase: 'uploading', sent, total }),
        )
        pendingUploadRef.current = null
        setUpload({ phase: 'done' })
      } catch (err) {
        // Surface on the uploading screen (retry) — the answers still save.
        console.error('Recording upload failed', err)
        setUpload({ phase: 'error' })
      }
    },
    [sessionToken, token],
  )

  const retryUpload = useCallback(() => {
    const pending = pendingUploadRef.current
    if (pending) void performUpload(pending.blob, pending.durationSeconds)
    else setUpload({ phase: 'done' })
  }, [performUpload])

  const stopRecordingAndUpload = useCallback(() => {
    const recorder = recorderRef.current
    if (!recorder || recorder.state === 'inactive') return
    const durationSeconds = (Date.now() - recordStartRef.current) / 1000
    // Flip to 'uploading' synchronously: `onstop` (and therefore the upload)
    // fires a tick after `recorder.stop()`, and a caller that finalizes
    // immediately — e.g. when the stimulus is the last screen before the
    // thank-you — must see an in-flight upload rather than 'idle'. Otherwise
    // the journey could complete and the respondent could open their results
    // before the recording exists.
    setUpload({ phase: 'uploading', sent: 0, total: 0 })
    recorder.onstop = () => {
      stopStream()
      const type = recorder.mimeType || chunksRef.current[0]?.type || 'video/webm'
      const blob = new Blob(chunksRef.current, { type })
      if (blob.size > 0 && sessionToken) {
        pendingUploadRef.current = { blob, durationSeconds }
        void performUpload(blob, durationSeconds)
      } else {
        setUpload({ phase: 'done' })
      }
    }
    try {
      recorder.stop()
    } catch {
      // Already stopped — onstop won't fire, so release the waiter ourselves.
      stopStream()
      setUpload({ phase: 'done' })
    }
  }, [sessionToken, performUpload, stopStream])

  // ── submit answers (no completion — questions may come before the stimulus) ──
  const submitAnswers = useCallback(async () => {
    const payload: RespondentAnswer[] = Object.entries(answers).map(([qid, a]) => ({
      question_id: Number(qid),
      choices: a.choices,
      text_answer: a.text_answer,
    }))
    if (!payload.length) return
    try {
      await respondentService.submitAnswers(token, sessionToken, payload)
    } catch (err) {
      console.error('Submit answers failed', err)
    }
  }, [answers, sessionToken, token])

  // ── finalize: show the upload screen; it completes the journey once the
  // recording is safely on the server ──
  const finalize = useCallback(() => {
    setStep('uploading')
  }, [])

  // Once the upload settles while the uploading screen is showing, mark the
  // journey complete and move on to the thank-you. Completion deliberately
  // waits for the upload so the respondent can't open results before the
  // recording exists.
  const completedRef = useRef(false)
  const completeAndThank = useCallback(async () => {
    if (!completedRef.current) {
      completedRef.current = true
      try {
        await respondentService.complete(token, sessionToken)
      } catch (err) {
        console.error('Finalize failed', err)
      }
    }
    setStep('thanks')
  }, [sessionToken, token])

  useEffect(() => {
    // 'idle' means no recording was ever started — nothing to wait for.
    if (step === 'uploading' && (upload.phase === 'done' || upload.phase === 'idle')) {
      void completeAndThank()
    }
  }, [step, upload.phase, completeAndThank])

  // ── advance to the next screen, finalizing if that screen is the thank-you ──
  const advanceFrom = useCallback(
    async (from: Step) => {
      const nxt = nextStep(from)
      if (nxt === 'thanks') finalize()
      else setStep(nxt)
    },
    [nextStep, finalize],
  )

  const handleQuestionsDone = useCallback(async () => {
    await submitAnswers()
    await advanceFrom('questions')
  }, [submitAnswers, advanceFrom])

  // ── render ──
  let content: React.ReactNode = null

  if (step === 'loading') {
    content = (
      <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', bgcolor: '#fff' }}>
        <CircularProgress sx={{ color: C.coral }} />
      </Box>
    )
  } else if (step === 'error') {
    content = (
      <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', bgcolor: '#fff', p: 3 }}>
        <Stack spacing={1.5} alignItems="center" sx={{ textAlign: 'center', maxWidth: 420 }}>
          <Lockup />
          <Typography variant="h5" sx={{ mt: 2 }}>
            Nie można otworzyć badania
          </Typography>
          <Typography sx={{ color: C.ink56 }}>
            {errorMsg || 'Link jest nieprawidłowy lub badanie zostało zakończone.'}
          </Typography>
        </Stack>
      </Box>
    )
  } else if (study) {
    // The calibration screen renders its own full-screen overlay (no stepper),
    // so it's excluded from the visible step count — keeps "Krok N z 6" honest.
    const visibleSteps: Step[] = sequence.filter((s) => s !== 'calibration')
    const stepIdx = Math.max(0, visibleSteps.indexOf(step))
    if (step === 'landing' && (study.requires_desktop || eyeTracking) && isMobileDevice()) {
      // Two independent reasons a phone can't run this study, turned away
      // here — before consent/camera — so it never spends one of the slots:
      // a clickable prototype needs a mouse-sized target (decision
      // 2026-09-23), and gaze calibration is meaningless on a handheld
      // screen (Z-65). Figma takes priority in the copy when a study somehow
      // has both, since it's the more specific failure mode.
      content = (
        <DesktopOnlyScreen
          body={
            study.requires_desktop
              ? 'W tym badaniu klikasz w prototyp aplikacji, a to działa tylko na komputerze z kamerą. Skopiuj link i otwórz go na laptopie lub komputerze stacjonarnym.'
              : 'To badanie śledzi, gdzie patrzysz na ekranie, dlatego działa tylko na komputerze z kamerą. Na telefonie nie da się tego dokładnie zmierzyć. Skopiuj link i otwórz go na laptopie lub komputerze stacjonarnym.'
          }
        />
      )
    } else if (step === 'landing') {
      content = <Landing study={study} onStart={() => setStep('consent')} stepIdx={stepIdx} />
    } else if (step === 'consent') {
      content = (
        <Consent
          onAgree={handleConsent}
          requireCode={study.require_access_code}
          eyeTracking={eyeTracking}
          client={study.client}
          stepIdx={stepIdx}
        />
      )
    } else if (step === 'tech') {
      content = (
        <TechCheck
          streamRef={streamRef}
          hasAudio={studyHasAudio(study)}
          onReady={() => {
            // Eye-tracking studies start recording at the calibration screen
            // instead — after fullscreen + orientation lock settle — so the whole
            // clip is one orientation (a mid-recording flip corrupts it).
            if (!eyeTracking) startRecording()
            void advanceFrom('tech')
          }}
          stepIdx={stepIdx}
        />
      )
    } else if (step === 'calibration') {
      content = (
        <EyeTrackingCalibration
          study={study}
          streamRef={streamRef}
          recordStartRef={recordStartRef}
          onBegin={startRecording}
          onComplete={(points) => {
            calibrationRef.current = points
            void advanceFrom('calibration')
          }}
        />
      )
    } else if (step === 'stimulus') {
      content = (
        <StimulusScreen
          study={study}
          recordStartRef={recordStartRef}
          onMarker={(m) => markersRef.current.push(m)}
          prototypeEmbeds={prototypeEmbedsRef.current}
          onPrototypeSession={(session) => prototypeSessionsRef.current.push(session)}
          onDone={() => {
            stopRecordingAndUpload()
            void advanceFrom('stimulus')
          }}
        />
      )
    } else if (step === 'questions') {
      content = (
        <Questions
          questions={study.questions}
          answers={answers}
          setAnswers={setAnswers}
          onFinish={handleQuestionsDone}
          stepIdx={stepIdx}
        />
      )
    } else if (step === 'uploading') {
      content = (
        <UploadingScreen
          upload={upload}
          onRetry={retryUpload}
          onSkip={() => void completeAndThank()}
        />
      )
    } else if (step === 'thanks') {
      // Results open in their own tab at a dedicated, refreshable URL so the
      // respondent can reload (or bookmark/share) them without being dropped
      // back to the start of the one-shot journey. The session token carries
      // the identity the results endpoint needs.
      const resultsHref =
        `${window.location.origin}${window.location.pathname}` +
        `#/r/${encodeURIComponent(token)}/results?s=${encodeURIComponent(sessionToken)}`
      content = <ThankYou study={study} resultsHref={resultsHref} />
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={RESPONDENT_KEYFRAMES} />
      {content}
    </ThemeProvider>
  )
}

// ───────────────────────────── Screen 1: Landing ───────────────────────────

function NeedItem({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{ p: 1.5, border: `1px solid ${C.ink08}`, borderRadius: 2, bgcolor: '#fff' }}
    >
      <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: C.coralSoft, display: 'grid', placeItems: 'center', color: C.coral }}>
        {icon}
      </Box>
      <Typography sx={{ fontSize: 14, color: C.ink }}>{label}</Typography>
    </Stack>
  )
}

export function Landing({ study, onStart, stepIdx }: { study: RespondentStudy; onStart: () => void; stepIdx: number }) {
  const nQuestions = study.questions.length
  return (
    <LightShell step={stepIdx}>
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', px: { xs: 2.5, md: 7 }, py: { xs: 3, md: 6 } }}>
        <Stack spacing={3} sx={{ maxWidth: 640, width: '100%' }}>
          <Typography sx={{ fontSize: 12, letterSpacing: '.1em', textTransform: 'uppercase', color: C.coral, fontWeight: 700 }}>
            Zaproszenie do badania
          </Typography>
          <Typography variant="h3" sx={{ fontSize: { xs: 30, md: 44 }, lineHeight: 1.1 }}>
            {study.name || 'Badanie reklamy — kilka minut Twojego czasu'}
          </Typography>
          {study.client && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ px: 1.25, py: 0.5, borderRadius: 34, bgcolor: C.gray50, border: `1px solid ${C.ink08}`, fontSize: 13, color: C.ink72 }}>
                {study.client}
              </Box>
            </Stack>
          )}
          <Typography sx={{ fontSize: { xs: 15, md: 18 }, lineHeight: 1.6, color: C.ink72 }}>
            {study.description ||
              `Obejrzysz krótki materiał${nQuestions ? ` i odpowiesz na ${nQuestions} ${nQuestions === 1 ? 'pytanie' : 'pytania'}` : ''}. W tle Twoja kamera będzie rejestrować mimikę twarzy, aby określić emocje wywołane przez materiał.`}
          </Typography>
          <Box>
            <Typography sx={{ fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: C.ink48, fontWeight: 700, mb: 1 }}>
              Czego potrzebujesz
            </Typography>
            <Stack spacing={1}>
              <NeedItem icon={<VideocamOutlinedIcon sx={{ fontSize: 17 }} />} label="Kamera w urządzeniu" />
              <NeedItem icon={<WbSunnyOutlinedIcon sx={{ fontSize: 17 }} />} label="Dobre, równe światło" />
              <NeedItem icon={<ScheduleOutlinedIcon sx={{ fontSize: 17 }} />} label="Około 5 minut" />
            </Stack>
          </Box>
          <Box>
            <Button variant="contained" size="large" onClick={onStart} endIcon={<ArrowForwardIcon />} sx={{ px: 4, py: 1.25 }}>
              Rozpocznij badanie
            </Button>
          </Box>
        </Stack>
      </Box>
    </LightShell>
  )
}

// ───────────────────────────── Screen 2: Consent ───────────────────────────

const COLLECTED = [
  { label: 'Nagranie wideo Twojej twarzy', sub: 'Tylko podczas oglądania materiału.' },
  { label: 'Analiza emocji', sub: 'Uśmiech, zaskoczenie, koncentracja, neutralność itd.' },
  { label: 'Twoje odpowiedzi', sub: 'Wybory w pytaniach po materiale.' },
]
const COLLECTED_GAZE = {
  label: 'Kierunek wzroku (eye tracking)',
  sub: 'Przybliżone miejsce patrzenia na ekranie, po krótkiej kalibracji.',
}

/** RODO (GDPR art. 13) information clause shown above the consent checkboxes.
 *
 *  The controller is whoever commissioned the study — we know them only by the
 *  study's `client` field, so fall back to naming the researcher generically
 *  rather than printing an empty name. Facewise itself is the processor.
 */
function rodoClause(client: string, eyeTracking: boolean): { title: string; body: string }[] {
  const controller = (client || '').trim() || 'organizator badania — badacz, który udostępnił Ci ten link'
  const scope =
    'nagranie wideo Twojej twarzy, wskaźniki emocji wyliczone z mimiki' +
    (eyeTracking ? ', przybliżony kierunek wzroku' : '') +
    ', Twoje odpowiedzi na pytania oraz typ urządzenia.'
  return [
    {
      title: 'Kto przetwarza Twoje dane',
      body:
        `Administratorem danych jest ${controller}. Platforma Facewise przetwarza ` +
        'dane wyłącznie na jego polecenie, jako podmiot przetwarzający.',
    },
    {
      title: 'Po co i na jakiej podstawie',
      body:
        'Dane są przetwarzane wyłącznie po to, żeby przeprowadzić to badanie i opracować ' +
        'jego wyniki. Podstawą jest Twoja zgoda — art. 6 ust. 1 lit. a RODO, a dla nagrania ' +
        'twarzy i analizy mimiki, które są danymi biometrycznymi, wyraźna zgoda z art. 9 ' +
        'ust. 2 lit. a RODO.',
    },
    { title: 'Jakie dane zbieramy', body: `Zbieramy ${scope}` },
    {
      title: 'Komu je udostępniamy',
      body:
        'Dostęp ma badacz prowadzący to badanie oraz dostawcy infrastruktury IT, na której ' +
        'działa platforma (serwery w Unii Europejskiej). Nie sprzedajemy Twoich danych ' +
        'i nie wykorzystujemy ich do reklamy.',
    },
    {
      title: 'Jak długo je przechowujemy',
      body:
        'Do zakończenia badania i opracowania jego wyników, a jeśli wcześniej wycofasz ' +
        'zgodę — do momentu jej wycofania.',
    },
    {
      title: 'Twoje prawa',
      body:
        'Masz prawo dostępu do swoich danych, ich sprostowania, usunięcia, ograniczenia ' +
        'przetwarzania, przenoszenia oraz sprzeciwu. Zgodę możesz wycofać w każdej chwili — ' +
        'nie wpływa to na zgodność z prawem przetwarzania sprzed jej wycofania. Możesz też ' +
        'złożyć skargę do Prezesa Urzędu Ochrony Danych Osobowych. Aby skorzystać z tych ' +
        'praw, skontaktuj się z badaczem, który przekazał Ci link do badania.',
    },
    {
      title: 'Dobrowolność',
      body:
        'Udział jest w pełni dobrowolny, ale bez tych zgód nie możemy przeprowadzić badania. ' +
        'Nie podejmujemy wobec Ciebie żadnych automatycznych decyzji wywołujących skutki prawne.',
    },
  ]
}
export function Consent({
  onAgree,
  requireCode,
  eyeTracking,
  client,
  stepIdx,
}: {
  onAgree: (accessCode: string) => Promise<void>
  requireCode: boolean
  eyeTracking: boolean
  client: string
  stepIdx: number
}) {
  const collected = eyeTracking ? [...COLLECTED, COLLECTED_GAZE] : COLLECTED
  const clause = useMemo(() => rodoClause(client, eyeTracking), [client, eyeTracking])
  // a = biometric processing (art. 9), b = 18+, rodo = clause read (art. 13)
  const [a, setA] = useState(false)
  const [b, setB] = useState(false)
  const [rodo, setRodo] = useState(false)
  const [clauseOpen, setClauseOpen] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [codeErr, setCodeErr] = useState(false)

  const codeOk = !requireCode || code.trim().length > 0

  const submit = async () => {
    setBusy(true)
    setErr('')
    setCodeErr(false)
    try {
      await onAgree(code.trim())
    } catch (e) {
      // On this endpoint a 403 always means the access code was rejected.
      if (e instanceof ApiError && e.status === 403) {
        setCodeErr(true)
        setErr('Nieprawidłowy kod dostępu. Sprawdź kod otrzymany od badacza i spróbuj ponownie.')
      } else if (e instanceof Error && e.message && !e.message.startsWith('HTTP error')) {
        setErr(e.message)
      } else {
        setErr('Coś poszło nie tak. Spróbuj ponownie.')
      }
      setBusy(false)
    }
  }

  return (
    <LightShell step={stepIdx}>
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', px: { xs: 2.5, md: 7 }, py: { xs: 3, md: 5 }, overflow: 'auto' }}>
        <Stack spacing={3} sx={{ maxWidth: 640, width: '100%' }}>
          <Box>
            <Typography sx={{ fontSize: 12, letterSpacing: '.1em', textTransform: 'uppercase', color: C.coral, fontWeight: 700 }}>
              Krok 2 · Zgoda
            </Typography>
            <Typography variant="h4" sx={{ fontSize: { xs: 24, md: 32 }, mt: 1 }}>
              Zgoda na udział w badaniu
            </Typography>
            <Typography sx={{ color: C.ink72, mt: 1, lineHeight: 1.6 }}>
              Podczas badania Twoja kamera rejestruje mimikę twarzy. Potrzebujemy Twojej zgody, zanim zaczniemy.
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: C.ink48, fontWeight: 700, mb: 1 }}>
              Co zbieramy
            </Typography>
            <Stack spacing={1}>
              {collected.map((it) => (
                <Stack key={it.label} direction="row" spacing={1.5} sx={{ p: 1.5, border: `1px solid ${C.ink08}`, borderRadius: 2 }}>
                  <CheckIcon sx={{ fontSize: 18, color: C.coral, mt: 0.25 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{it.label}</Typography>
                    <Typography sx={{ fontSize: 13, color: C.ink56 }}>{it.sub}</Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Box>

          {/* RODO (art. 13) information clause — collapsed by default so the
              screen stays scannable, but reachable before the consent is given. */}
          <Box sx={{ border: `1px solid ${C.ink08}`, borderRadius: 3, overflow: 'hidden' }}>
            <Box
              component="button"
              type="button"
              onClick={() => setClauseOpen((v) => !v)}
              aria-expanded={clauseOpen}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                p: 2,
                bgcolor: 'transparent',
                border: 0,
                cursor: 'pointer',
                textAlign: 'left',
                font: 'inherit',
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: C.ink48, fontWeight: 700 }}>
                  Klauzula informacyjna RODO
                </Typography>
                <Typography sx={{ fontSize: 13, color: C.ink56, mt: 0.25 }}>
                  Kto przetwarza Twoje dane, w jakim celu i jakie masz prawa.
                </Typography>
              </Box>
              <ExpandMoreIcon
                sx={{
                  fontSize: 22,
                  color: C.ink48,
                  flex: '0 0 auto',
                  transition: 'transform .2s',
                  transform: clauseOpen ? 'rotate(180deg)' : 'none',
                }}
              />
            </Box>
            <Collapse in={clauseOpen} unmountOnExit>
              <Stack spacing={1.75} sx={{ px: 2, pb: 2 }}>
                {clause.map((sec) => (
                  <Box key={sec.title}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{sec.title}</Typography>
                    <Typography sx={{ fontSize: 13, color: C.ink56, lineHeight: 1.6 }}>{sec.body}</Typography>
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </Box>

          <Box sx={{ bgcolor: C.gray50, border: `1px solid ${C.ink08}`, borderRadius: 3, p: 2.5 }}>
            {requireCode && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: C.ink48, fontWeight: 700, mb: 1 }}>
                  Kod dostępu
                </Typography>
                <TextField
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value)
                    setCodeErr(false)
                  }}
                  placeholder="Wpisz kod otrzymany od badacza"
                  fullWidth
                  autoComplete="off"
                  error={codeErr}
                  sx={{ bgcolor: '#fff', borderRadius: 1.5 }}
                />
              </Box>
            )}
            <FormControlLabel
              control={<Checkbox checked={a} onChange={(e) => setA(e.target.checked)} sx={{ p: 0, mr: 1.25, color: C.ink32, '&.Mui-checked': { color: C.coral } }} />}
              label={
                <Typography sx={{ fontSize: 14, lineHeight: '24px' }}>
                  Rozumiem, jakie dane są zbierane i <b>wyrażam zgodę na przetwarzanie moich danych
                  biometrycznych</b> — nagrania twarzy i analizy mimiki — na potrzeby tego badania.
                </Typography>
              }
              sx={{ alignItems: 'flex-start', m: 0, mb: 1.5 }}
            />
            <FormControlLabel
              control={<Checkbox checked={b} onChange={(e) => setB(e.target.checked)} sx={{ p: 0, mr: 1.25, color: C.ink32, '&.Mui-checked': { color: C.coral } }} />}
              label={<Typography sx={{ fontSize: 14, lineHeight: '24px' }}><b>Mam ukończone 18 lat.</b></Typography>}
              sx={{ alignItems: 'flex-start', m: 0, mb: 1.5 }}
            />
            <FormControlLabel
              control={<Checkbox checked={rodo} onChange={(e) => setRodo(e.target.checked)} sx={{ p: 0, mr: 1.25, color: C.ink32, '&.Mui-checked': { color: C.coral } }} />}
              label={
                <Typography sx={{ fontSize: 14, lineHeight: '24px' }}>
                  <b>Zapoznałem/am się z klauzulą informacyjną RODO</b> — wiem, kto przetwarza moje
                  dane, w jakim celu i jakie mam prawa, w tym prawo do wycofania zgody.
                </Typography>
              }
              sx={{ alignItems: 'flex-start', m: 0 }}
            />
            {err && <Typography sx={{ color: C.danger, fontSize: 13, mt: 1 }}>{err}</Typography>}
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={!(a && b && rodo) || !codeOk || busy}
              onClick={submit}
              endIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ArrowForwardIcon />}
              sx={{ mt: 2, py: 1.25 }}
            >
              Wyrażam zgodę i przechodzę dalej
            </Button>
          </Box>
        </Stack>
      </Box>
    </LightShell>
  )
}

// ─────────────────────────── Screen 3: Tech check ──────────────────────────

function TechCheck({
  streamRef,
  hasAudio,
  onReady,
  stepIdx,
}: {
  streamRef: React.MutableRefObject<MediaStream | null>
  hasAudio: boolean
  onReady: () => void
  stepIdx: number
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  // 'prompt'  → explain the upcoming browser permission dialog before triggering it
  // 'starting'→ getUserMedia in flight (browser dialog is showing)
  // 'denied'  → user blocked the camera; offer a retry
  // 'detecting'/'ready' → camera live, looking for / found a face
  const [state, setState] = useState<'prompt' | 'starting' | 'denied' | 'detecting' | 'ready'>('prompt')
  const [faceVisible, setFaceVisible] = useState(false)
  const detectTimer = useRef<number | null>(null)
  const unmounted = useRef(false)

  useEffect(() => {
    unmounted.current = false
    return () => {
      unmounted.current = true
      if (detectTimer.current) window.clearInterval(detectTimer.current)
    }
  }, [])

  const requestCamera = useCallback(async () => {
    if (detectTimer.current) {
      window.clearInterval(detectTimer.current)
      detectTimer.current = null
    }
    setFaceVisible(false)
    setState('starting')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
        audio: false,
      })
      if (unmounted.current) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play().catch(() => {})
      }
      setState('detecting')
      try {
        await loadFaceModels('/models')
      } catch {
        // If models fail, allow proceeding on camera presence alone.
        setState('ready')
        setFaceVisible(true)
        return
      }
      let hits = 0
      detectTimer.current = window.setInterval(async () => {
        if (!videoRef.current || videoRef.current.readyState < 2) return
        try {
          const faces = await detectFaces(videoRef.current)
          if (faces.length > 0) {
            hits += 1
            if (hits >= 2) {
              setFaceVisible(true)
              setState('ready')
            }
          } else {
            hits = 0
            setFaceVisible(false)
          }
        } catch {
          /* ignore a single frame failure */
        }
      }, 500)
    } catch {
      if (!unmounted.current) setState('denied')
    }
  }, [streamRef])

  return (
    <LightShell step={stepIdx}>
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', px: { xs: 2.5, md: 7 }, py: { xs: 3, md: 5 } }}>
        <Stack spacing={3} sx={{ maxWidth: 560, width: '100%', alignItems: 'center', textAlign: 'center' }}>
          <Box sx={{ width: '100%' }}>
            <Typography sx={{ fontSize: 12, letterSpacing: '.1em', textTransform: 'uppercase', color: C.coral, fontWeight: 700 }}>
              Krok 3 · Sprawdzenie kamery
            </Typography>
            <Typography variant="h4" sx={{ fontSize: { xs: 24, md: 32 }, mt: 1 }}>
              Ustaw się wygodnie przed kamerą
            </Typography>
          </Box>

          <Box
            sx={{
              position: 'relative',
              width: '100%',
              // Smaller on phones so the whole frame stays comfortably on screen.
              maxWidth: { xs: 300, sm: '100%' },
              aspectRatio: '4 / 3',
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: C.navy900,
              border: `2px solid ${faceVisible ? C.success : C.ink08}`,
            }}
          >
            <Box
              component="video"
              ref={videoRef}
              muted
              playsInline
              // 'contain' shows the entire camera frame (letterboxed against the
              // navy backdrop) instead of cropping to the centre like 'cover'.
              sx={{ width: '100%', height: '100%', objectFit: 'contain', transform: 'scaleX(-1)' }}
            />
            {(state === 'prompt' || state === 'starting') && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', p: 3, color: '#fff', bgcolor: 'rgba(11,12,30,.9)' }}>
                <Stack spacing={1.25} alignItems="center" sx={{ maxWidth: 380 }}>
                  <VideocamOutlinedIcon sx={{ fontSize: 40, color: C.coral }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Potrzebujemy dostępu do kamery</Typography>
                  <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,.72)', lineHeight: 1.5 }}>
                    Gdy klikniesz <b>„Włącz kamerę”</b>, przeglądarka zapyta o pozwolenie.
                    W okienku, które się pojawi, wybierz <b>„Zezwól”</b>, aby kontynuować.
                  </Typography>
                </Stack>
              </Box>
            )}
            {state === 'denied' && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', p: 3, color: '#fff', bgcolor: 'rgba(11,12,30,.9)' }}>
                <Stack spacing={1.25} alignItems="center" sx={{ maxWidth: 380 }}>
                  <VideocamOutlinedIcon sx={{ fontSize: 40, color: C.warning }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Brak dostępu do kamery</Typography>
                  <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,.72)', lineHeight: 1.5 }}>
                    Dostęp do kamery został zablokowany. Kliknij <b>„Spróbuj ponownie”</b> i wybierz <b>„Zezwól”</b>.
                    Jeśli okienko się nie pojawia, kliknij ikonę kamery na pasku adresu przeglądarki i włącz dostęp.
                  </Typography>
                </Stack>
              </Box>
            )}
            {state !== 'denied' && state !== 'prompt' && state !== 'starting' && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 34,
                  bgcolor: faceVisible ? 'rgba(95,179,168,.9)' : 'rgba(11,12,30,.7)',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  whiteSpace: 'nowrap',
                }}
              >
                {faceVisible ? <CheckIcon sx={{ fontSize: 14 }} /> : null}
                {faceVisible ? 'Twarz wykryta' : 'Wyśrodkuj twarz w kadrze'}
              </Box>
            )}
          </Box>

          {hasAudio && (
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="flex-start"
              sx={{
                width: '100%',
                maxWidth: 360,
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'rgba(234,80,72,.08)',
                border: `1px solid ${C.coral}33`,
                textAlign: 'left',
              }}
            >
              <VolumeUpOutlinedIcon sx={{ fontSize: 22, color: C.coral, mt: '1px', flexShrink: 0 }} />
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.ink }}>Włącz dźwięk</Typography>
                <Typography sx={{ fontSize: 12.5, color: C.ink56, mt: 0.25 }}>
                  Ten materiał zawiera dźwięk. Upewnij się, że telefon nie jest wyciszony i podgłośnij urządzenie.
                </Typography>
              </Box>
            </Stack>
          )}

          {state === 'prompt' && (
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={requestCamera}
              startIcon={<VideocamOutlinedIcon />}
              sx={{ py: 1.25, maxWidth: 360 }}
            >
              Włącz kamerę
            </Button>
          )}
          {state === 'starting' && (
            <Button variant="contained" size="large" fullWidth disabled sx={{ py: 1.25, maxWidth: 360 }}>
              Czekam na pozwolenie…
            </Button>
          )}
          {state === 'denied' && (
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={requestCamera}
              startIcon={<ReplayRoundedIcon />}
              sx={{ py: 1.25, maxWidth: 360 }}
            >
              Spróbuj ponownie
            </Button>
          )}
          {(state === 'detecting' || state === 'ready') && (
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={state !== 'ready'}
              onClick={onReady}
              endIcon={<ArrowForwardIcon />}
              sx={{ py: 1.25, maxWidth: 360 }}
            >
              Wszystko gotowe — zaczynamy
            </Button>
          )}
        </Stack>
      </Box>
    </LightShell>
  )
}

// ──────────────────────── Screen 4: Stimulus + recording ───────────────────

/** How long the empty sample "film" runs when a project has no stimulus. */
const SAMPLE_MS = 12000

/**
 * Fallback when a project has no stimulus configured: an empty film, i.e. a
 * black frame with a running progress bar and clock, so the step reads as
 * "a video plays here" rather than as a placeholder card.
 */
function SampleStimulus() {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const t0 = performance.now()
    const id = window.setInterval(() => setElapsed(Math.min(SAMPLE_MS, performance.now() - t0)), 250)
    return () => window.clearInterval(id)
  }, [])
  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', bgcolor: '#000' }}>
      <Box
        sx={{
          position: 'absolute',
          left: 16,
          right: 16,
          bottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          color: 'rgba(255,255,255,.55)',
          fontSize: 12,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span>{fmtTime(elapsed / 1000)}</span>
        <Box sx={{ flex: 1, height: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,.18)', overflow: 'hidden' }}>
          <Box
            sx={{
              height: '100%',
              bgcolor: 'rgba(255,255,255,.7)',
              transformOrigin: 'left',
              animation: `sampleFilm ${SAMPLE_MS}ms linear forwards`,
              '@keyframes sampleFilm': { from: { transform: 'scaleX(0)' }, to: { transform: 'scaleX(1)' } },
            }}
          />
        </Box>
        <span>{fmtTime(SAMPLE_MS / 1000)}</span>
      </Box>
    </Box>
  )
}

export function StimulusScreen({
  study,
  recordStartRef,
  onMarker,
  prototypeEmbeds,
  onPrototypeSession,
  onDone,
}: {
  study: RespondentStudy
  recordStartRef: React.MutableRefObject<number>
  onMarker: (m: StimulusMarker) => void
  prototypeEmbeds: Record<string, string>
  onPrototypeSession: (session: PrototypeSession) => void
  onDone: () => void
}) {
  const playable = study.stimuli.filter((s) => s.url || s.payload)
  const [index, setIndex] = useState(0)
  const current = playable[index]
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const doneRef = useRef(false)
  const [needsTap, setNeedsTap] = useState(false)

  // 3-2-1 countdown before the first stimulus, so the respondent can settle in
  // front of the camera. The recording is already running (it starts at the
  // tech check), so the stimulus itself — and its marker window — is held back
  // until the countdown finishes.
  const [countdown, setCountdown] = useState(3)
  const counting = countdown > 0
  useEffect(() => {
    if (!counting) return
    const t = window.setTimeout(() => setCountdown((c) => c - 1), 1000)
    return () => window.clearTimeout(t)
  }, [counting, countdown])

  // Captured offset (ms from recording start) of the moment playback ACTUALLY
  // began for the current stimulus. Video may take seconds to load/buffer or
  // wait for a tap-to-play gesture, so mount time overstates the on-screen
  // window. -1 means "not yet playing" → fall back to the mount-time start.
  const playStartRef = useRef(-1)
  useEffect(() => {
    playStartRef.current = -1
  }, [current?.id])

  // Record each stimulus's on-screen window relative to the recording start.
  // The cleanup fires when the stimulus changes and on unmount (after the last
  // clip), so every stimulus contributes a marker.
  //
  // start_ms is taken at FIRST PLAYBACK, not mount: the backend derives
  // duration_ms from this window, and counting load/buffer/tap-wait time as
  // "on screen" makes a 5s video look like 8s. We prefer the captured play-start
  // (playStartRef) and only fall back to the mount-time value when playback
  // never fired (e.g. image/text stimuli, which start showing at mount).
  //
  // The marker is closed explicitly by advance() BEFORE onDone — not only by
  // the effect cleanup. onDone stops the MediaRecorder, whose onstop starts
  // the upload, and that reads the marker list before React gets round to
  // unmounting this screen; a marker pushed from the cleanup alone missed the
  // upload every time (Z-119: no markers → no gaze, a guessed emotion window).
  // The cleanup stays as the fallback for an unmount advance() didn't see.
  const openMarkerRef = useRef<{ id: number; order: number; recordStart: number; mountStart: number } | null>(null)
  const closeMarker = useCallback(() => {
    const open = openMarkerRef.current
    if (!open) return
    openMarkerRef.current = null
    const end = open.recordStart ? Date.now() - open.recordStart : open.mountStart
    const start = playStartRef.current >= 0 ? playStartRef.current : open.mountStart
    const startMs = Math.max(0, Math.round(start))
    const endMs = Math.max(0, Math.round(end))
    onMarker({
      stimulus_id: open.id,
      order: open.order,
      start_ms: startMs,
      // Guarantee start_ms <= end_ms even if a clock hiccup inverts them.
      end_ms: Math.max(startMs, endMs),
    })
  }, [onMarker])

  useEffect(() => {
    if (!current || counting) return
    const recordStart = recordStartRef.current
    openMarkerRef.current = {
      id: current.id,
      order: current.order,
      recordStart,
      mountStart: recordStart ? Date.now() - recordStart : 0,
    }
    return closeMarker
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, counting])

  // First time the <video> reports it is actually playing, capture the offset so
  // the marker's start_ms reflects real playback rather than mount/buffer time.
  const handleVideoPlaying = useCallback(() => {
    if (playStartRef.current >= 0) return
    const recordStart = recordStartRef.current
    playStartRef.current = recordStart ? Date.now() - recordStart : 0
  }, [recordStartRef])

  const advance = useCallback(() => {
    if (doneRef.current) return
    closeMarker()
    if (index + 1 < playable.length) {
      setIndex((i) => i + 1)
    } else {
      doneRef.current = true
      onDone()
    }
  }, [index, playable.length, onDone, closeMarker])

  // Image / sample stimuli are time-driven.
  useEffect(() => {
    if (counting) return undefined
    if (current && (current.type === 'image' || current.type === 'text')) {
      const ms = current.exposure_ms || 5000
      const t = window.setTimeout(advance, ms)
      return () => window.clearTimeout(t)
    }
    if (!current) {
      // No stimulus configured — show the sample for ~12s.
      const t = window.setTimeout(advance, SAMPLE_MS)
      return () => window.clearTimeout(t)
    }
    return undefined
  }, [current, advance, counting])

  const ytId = current && current.type === 'url' ? youTubeId(current.url) : null
  const isYouTube = !!ytId
  const isVideo = current && (current.type === 'video' || current.type === 'url') && !!current.url && !isYouTube

  // Attempt autoplay; if the browser blocks it, show a tap-to-play overlay.
  useEffect(() => {
    if (!isVideo || counting) return
    setNeedsTap(false)
    const v = videoRef.current
    if (!v) return
    const p = v.play()
    if (p && typeof p.then === 'function') {
      p.catch(() => setNeedsTap(true))
    }
  }, [isVideo, current?.id, counting])

  const tapToPlay = () => {
    const v = videoRef.current
    if (!v) return
    v.play()
      .then(() => setNeedsTap(false))
      .catch(() => setNeedsTap(true))
  }

  return (
    <Box sx={{ position: 'fixed', inset: 0, width: '100vw', height: '100dvh', bgcolor: '#000', color: '#fff', overflow: 'hidden' }}>
      {counting ? (
        <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100%' }}>
          <Typography
            sx={{
              fontSize: 13,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              fontWeight: 700,
              color: 'rgba(255,255,255,.6)',
            }}
          >
            Przygotuj się
          </Typography>
          <Typography
            key={countdown}
            sx={{
              fontFamily: '"Roboto"',
              fontWeight: 700,
              fontSize: { xs: 110, md: 160 },
              lineHeight: 1,
              animation: 'countPop .9s ease-out both',
            }}
          >
            {countdown}
          </Typography>
          <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,.45)' }}>
            Materiał zaraz się rozpocznie
          </Typography>
        </Stack>
      ) : current && current.type === 'figma' ? (
        <FigmaTaskStimulus
          key={current.id}
          stimulus={current}
          embedUrl={prototypeEmbeds[String(current.id)]}
          recordStartRef={recordStartRef}
          // The window opens on "Zaczynam", not when the task text appears:
          // reading the brief is not time spent in the prototype.
          onStart={handleVideoPlaying}
          onSession={onPrototypeSession}
          onDone={advance}
        />
      ) : isYouTube ? (
        <YouTubeStimulus key={current!.id} videoId={ytId!} onEnded={advance} onFirstPlay={handleVideoPlaying} />
      ) : isVideo ? (
        <Box
          key={current!.id}
          component="video"
          ref={videoRef}
          src={current!.url}
          autoPlay
          playsInline
          onPlaying={handleVideoPlaying}
          onEnded={advance}
          onError={advance}
          sx={{ width: '100%', height: '100%', objectFit: 'contain', bgcolor: '#000' }}
        />
      ) : current && current.type === 'image' && current.url ? (
        <Box component="img" src={current.url} alt={current.title} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      ) : current && current.type === 'text' ? (
        <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', p: { xs: 4, md: 8 } }}>
          <Typography sx={{ fontFamily: '"Roboto"', fontWeight: 700, fontSize: { xs: 32, md: 56 }, textAlign: 'center' }}>
            {current.payload}
          </Typography>
        </Box>
      ) : (
        <SampleStimulus />
      )}

      {/* Recording indicator overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          borderRadius: 34,
          bgcolor: 'rgba(234,80,72,.18)',
          border: '1px solid rgba(234,80,72,.4)',
          color: '#FFB1AC',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '.06em',
          zIndex: 3,
        }}
      >
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: C.coral, animation: 'recPulse 1.4s infinite' }} />
        REC
      </Box>

      {isVideo && needsTap && (
        <Box
          onClick={tapToPlay}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'rgba(0,0,0,.45)',
            cursor: 'pointer',
            zIndex: 4,
          }}
        >
          <Stack alignItems="center" spacing={1.5}>
            <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: C.coral, display: 'grid', placeItems: 'center' }}>
              <PlayArrowIcon sx={{ fontSize: 40, color: '#fff' }} />
            </Box>
            <Typography sx={{ color: '#fff', fontWeight: 700 }}>Dotknij, aby odtworzyć</Typography>
          </Stack>
        </Box>
      )}
    </Box>
  )
}

/** Minimal slice of the YT IFrame Player API we drive. */
type YTPlayer = {
  destroy?: () => void
  playVideo?: () => void
  seekTo?: (seconds: number, allowSeekAhead: boolean) => void
  getCurrentTime?: () => number
  getPlayerState?: () => number
}

/**
 * Plays a YouTube video inline via the IFrame Player API and calls `onEnded`
 * when it finishes (so the reel advances just like an uploaded video).
 *
 * The respondent must watch it straight through: native controls and keyboard
 * shortcuts are disabled, a transparent overlay swallows clicks/taps on the
 * iframe (so there is no way to pause or scrub), and a monotonic seek-guard
 * snaps playback back if the position ever jumps forward or backward. This
 * keeps every recording on the same timeline, so emotions line up cleanly with
 * the video when reviewing results.
 */
function YouTubeStimulus({
  videoId,
  onEnded,
  onFirstPlay,
}: {
  videoId: string
  onEnded: () => void
  // Fired once, the first time playback reaches PLAYING. Lets the caller record
  // the real playback start (not mount time) for the stimulus marker window.
  onFirstPlay?: () => void
}) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const playerRef = useRef<YTPlayer | null>(null)
  const onEndedRef = useRef(onEnded)
  onEndedRef.current = onEnded
  const onFirstPlayRef = useRef(onFirstPlay)
  onFirstPlayRef.current = onFirstPlay
  const firedFirstPlayRef = useRef(false)
  const [playing, setPlaying] = useState(false)

  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    const w = window as unknown as {
      YT?: {
        Player: new (el: Element, opts: object) => YTPlayer
        PlayerState?: { ENDED: number; PLAYING: number }
      }
    }
    let player: YTPlayer | null = null
    let cancelled = false
    let poll = 0
    let guard = 0
    // Furthest point reached during real playback; we never let the respondent
    // jump ahead of it or rewind behind it.
    let allowed = 0

    // YT replaces the mount node with an <iframe>; keep it OUT of React's tree
    // (append imperatively) so unmount cleanup never fights React reconciliation.
    const mount = document.createElement('div')
    mount.style.width = '100%'
    mount.style.height = '100%'
    host.appendChild(mount)

    const create = () => {
      if (cancelled || !w.YT) return
      player = new w.YT.Player(mount, {
        videoId,
        width: '100%',
        height: '100%',
        // controls/keyboard off so there is no scrubber to drag and arrow keys
        // can't seek; fs/annotations off to keep the frame clean.
        playerVars: {
          autoplay: 1,
          controls: 0,
          disablekb: 1,
          fs: 0,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          iv_load_policy: 3,
        },
        events: {
          onReady: (e: { target: YTPlayer }) => {
            playerRef.current = e.target
            try {
              e.target.playVideo?.()
            } catch {
              /* autoplay may be blocked; the tap overlay starts it instead */
            }
          },
          onStateChange: (e: { data: number }) => {
            if (e.data === w.YT?.PlayerState?.ENDED) onEndedRef.current()
            const isPlaying = e.data === w.YT?.PlayerState?.PLAYING
            setPlaying(isPlaying)
            // Capture the first real playback moment (after buffer/tap), once.
            if (isPlaying && !firedFirstPlayRef.current) {
              firedFirstPlayRef.current = true
              onFirstPlayRef.current?.()
            }
          },
        },
      })
      playerRef.current = player

      // Seek-guard: clamp playback to a forward-only timeline. Thresholds are
      // generous so normal buffering jitter never triggers a correction.
      guard = window.setInterval(() => {
        const p = playerRef.current
        if (!p?.getCurrentTime || !p.seekTo) return
        const t = p.getCurrentTime()
        if (!Number.isFinite(t)) return
        if (t > allowed + 1.5 || t < allowed - 1.5) {
          p.seekTo(allowed, true)
        } else if (t > allowed) {
          allowed = t
        }
      }, 250)
    }

    if (!w.YT && !document.getElementById('yt-iframe-api')) {
      const s = document.createElement('script')
      s.id = 'yt-iframe-api'
      s.src = 'https://www.youtube.com/iframe_api'
      document.head.appendChild(s)
    }

    if (w.YT?.Player) {
      create()
    } else {
      poll = window.setInterval(() => {
        if (w.YT?.Player) {
          window.clearInterval(poll)
          create()
        }
      }, 150)
    }

    return () => {
      cancelled = true
      if (poll) window.clearInterval(poll)
      if (guard) window.clearInterval(guard)
      playerRef.current = null
      try {
        player?.destroy?.()
      } catch {
        /* already torn down */
      }
    }
  }, [videoId])

  // Any tap re-issues play (harmless if already playing) so a blocked autoplay
  // starts on the respondent's first gesture — but the overlay never pauses or
  // seeks, so the video can only ever move forward.
  const handleTap = () => {
    try {
      playerRef.current?.playVideo?.()
    } catch {
      /* player not ready yet */
    }
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', bgcolor: '#000' }}>
      <Box ref={hostRef} sx={{ width: '100%', height: '100%', bgcolor: '#000' }} />
      {/* Transparent click/tap shield over the iframe: blocks pause, scrub,
          double-tap-seek and fullscreen while still capturing the start gesture. */}
      <Box
        onClick={handleTap}
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 2,
          cursor: 'default',
          display: 'grid',
          placeItems: 'center',
          bgcolor: playing ? 'transparent' : 'rgba(0,0,0,.45)',
        }}
      >
        {!playing && (
          <Stack alignItems="center" spacing={1.5}>
            <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: C.coral, display: 'grid', placeItems: 'center' }}>
              <PlayArrowIcon sx={{ fontSize: 40, color: '#fff' }} />
            </Box>
            <Typography sx={{ color: '#fff', fontWeight: 700 }}>Dotknij, aby odtworzyć</Typography>
          </Stack>
        )}
      </Box>
    </Box>
  )
}

// ──────────────────────────── Screen 5: Questions ──────────────────────────

export function Questions({
  questions,
  answers,
  setAnswers,
  onFinish,
  stepIdx,
}: {
  questions: RespondentQuestion[]
  answers: Record<number, { choices: number[]; text_answer: string }>
  setAnswers: React.Dispatch<React.SetStateAction<Record<number, { choices: number[]; text_answer: string }>>>
  onFinish: () => Promise<void>
  stepIdx: number
}) {
  const [idx, setIdx] = useState(0)
  const [busy, setBusy] = useState(false)

  // No questionnaire? Go straight to finishing.
  useEffect(() => {
    if (questions.length === 0) {
      setBusy(true)
      void onFinish()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (questions.length === 0) {
    return (
      <LightShell step={stepIdx}>
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
          <CircularProgress sx={{ color: C.coral }} />
        </Box>
      </LightShell>
    )
  }

  const q = questions[idx]
  const a = answers[q.id] || { choices: [], text_answer: '' }
  const isText = q.question_type === 'TEXT'
  const isMulti = q.question_type === 'MULTIPLE_CHOICE'
  const answered = isText ? true : a.choices.length > 0

  const setChoice = (value: number) => {
    setAnswers((prev) => {
      const cur = prev[q.id] || { choices: [], text_answer: '' }
      let choices: number[]
      if (isMulti) {
        choices = cur.choices.includes(value) ? cur.choices.filter((v) => v !== value) : [...cur.choices, value]
      } else {
        choices = [value]
      }
      return { ...prev, [q.id]: { ...cur, choices } }
    })
  }
  const setText = (text: string) => {
    setAnswers((prev) => ({ ...prev, [q.id]: { ...(prev[q.id] || { choices: [] }), text_answer: text } }))
  }

  const next = async () => {
    if (idx + 1 < questions.length) {
      setIdx((i) => i + 1)
    } else {
      setBusy(true)
      await onFinish()
    }
  }

  return (
    <LightShell step={stepIdx}>
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', px: { xs: 2.5, md: 7 }, py: { xs: 3, md: 5 } }}>
        <Stack spacing={3} sx={{ maxWidth: 700, width: '100%' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: 12, letterSpacing: '.1em', textTransform: 'uppercase', color: C.coral, fontWeight: 700 }}>
              Pytanie {idx + 1} z {questions.length}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {questions.map((_, i) => (
                <Box key={i} sx={{ width: 22, height: 4, borderRadius: 34, bgcolor: i < idx ? C.navy : i === idx ? C.coral : C.gray100 }} />
              ))}
            </Stack>
          </Stack>

          <Typography variant="h4" sx={{ fontSize: { xs: 22, md: 32 }, lineHeight: 1.25 }}>
            {q.text}
          </Typography>

          {isText ? (
            <TextField
              multiline
              minRows={5}
              placeholder="Możesz wpisać kilka słów… (opcjonalnie)"
              value={a.text_answer}
              onChange={(e) => setText(e.target.value)}
              fullWidth
            />
          ) : (
            <Stack spacing={1.25}>
              {q.choices.map((choice) => {
                const picked = a.choices.includes(choice.value)
                return (
                  <Box
                    key={choice.value}
                    component="button"
                    type="button"
                    onClick={() => setChoice(choice.value)}
                    sx={{
                      textAlign: 'left',
                      cursor: 'pointer',
                      minHeight: 56,
                      px: 2.25,
                      borderRadius: 2,
                      bgcolor: picked ? 'rgba(234,80,72,.06)' : '#fff',
                      border: `1.5px solid ${picked ? C.coral : C.ink08}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      fontFamily: '"Lato"',
                      fontSize: 15,
                      color: C.ink,
                      transition: 'all 140ms',
                    }}
                  >
                    <span>{choice.text}</span>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: isMulti ? '6px' : '50%',
                        border: `2px solid ${picked ? C.coral : C.ink32}`,
                        bgcolor: picked ? C.coral : 'transparent',
                        display: 'grid',
                        placeItems: 'center',
                        flex: '0 0 22px',
                      }}
                    >
                      {picked && <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />}
                    </Box>
                  </Box>
                )
              })}
            </Stack>
          )}

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              disabled={idx === 0 || busy}
              startIcon={<ArrowBackIcon />}
              sx={{ color: C.ink72 }}
            >
              Wstecz
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              size="large"
              disabled={!answered || busy}
              onClick={next}
              endIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ArrowForwardIcon />}
              sx={{ px: 4, py: 1.25 }}
            >
              {idx + 1 < questions.length ? 'Dalej' : 'Zakończ'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </LightShell>
  )
}

// ─────────────────────── Screen 5b: Uploading recording ────────────────────

function UploadingScreen({
  upload,
  onRetry,
  onSkip,
}: {
  upload: UploadState
  onRetry: () => void
  onSkip: () => void
}) {
  const isError = upload.phase === 'error'
  const sent = upload.phase === 'uploading' ? upload.sent : 0
  const total = upload.phase === 'uploading' ? upload.total : 0
  const pct = total > 0 ? Math.min(100, Math.round((sent / total) * 100)) : 0
  const mb = (bytes: number) => (bytes / (1024 * 1024)).toFixed(1)

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: '#fff', display: 'grid', placeItems: 'center', p: 3 }}>
      <Stack spacing={2.5} alignItems="center" sx={{ textAlign: 'center', maxWidth: 480, width: '100%' }}>
        <Box
          sx={{
            width: 84,
            height: 84,
            borderRadius: '50%',
            bgcolor: isError ? 'rgba(234,80,72,.12)' : C.coralSoft,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 42, color: C.coral }} />
        </Box>
        <Typography variant="h4" sx={{ fontSize: { xs: 26, md: 34 } }}>
          {isError ? 'Nie udało się wysłać nagrania' : 'Wysyłamy Twoje nagranie'}
        </Typography>

        {isError ? (
          <>
            <Typography sx={{ color: C.ink72, fontSize: 15, lineHeight: 1.6 }}>
              Sprawdź połączenie z internetem i spróbuj ponownie. Twoje odpowiedzi są już zapisane.
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <Button variant="contained" size="large" onClick={onRetry} startIcon={<ReplayRoundedIcon />} sx={{ px: 3.5, py: 1.25 }}>
                Spróbuj ponownie
              </Button>
              <Button size="large" onClick={onSkip} sx={{ color: C.ink56 }}>
                Zakończ bez nagrania
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <Box sx={{ width: '100%', maxWidth: 420 }}>
              <LinearProgress
                variant={total > 0 ? 'determinate' : 'indeterminate'}
                value={pct}
                sx={{
                  height: 10,
                  borderRadius: 34,
                  bgcolor: C.ink08,
                  '& .MuiLinearProgress-bar': { borderRadius: 34, bgcolor: C.coral },
                }}
              />
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: 13, color: C.ink56, fontVariantNumeric: 'tabular-nums' }}>
                  {total > 0 ? `${mb(sent)} / ${mb(total)} MB` : 'Przygotowujemy nagranie…'}
                </Typography>
                {total > 0 && (
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.ink72, fontVariantNumeric: 'tabular-nums' }}>
                    {pct}%
                  </Typography>
                )}
              </Stack>
            </Box>
            <Typography sx={{ color: C.ink72, fontSize: 15, lineHeight: 1.6 }}>
              <b>Nie zamykaj tej strony</b> — nagranie wysyła się prosto z Twojej przeglądarki.
              Gdy się prześle, resztą zajmie się już nasz serwer.
            </Typography>
          </>
        )}
      </Stack>
    </Box>
  )
}

// ──────────────────────────── Screen 6: Thank you ──────────────────────────

export function ThankYou({ study, resultsHref }: { study: RespondentStudy; resultsHref: string }) {
  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: '#fff', display: 'grid', placeItems: 'center', p: 3 }}>
      <Stack spacing={2.5} alignItems="center" sx={{ textAlign: 'center', maxWidth: 480 }}>
        <Box sx={{ width: 84, height: 84, borderRadius: '50%', bgcolor: 'rgba(95,179,168,.15)', display: 'grid', placeItems: 'center' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 48, color: C.success }} />
        </Box>
        <Typography variant="h3" sx={{ fontSize: { xs: 28, md: 38 } }}>
          Dziękujemy!
        </Typography>
        <Typography sx={{ color: C.ink72, fontSize: 16, lineHeight: 1.6 }}>
          Twoje odpowiedzi i nagranie zostały zapisane{study.client ? ` dla ${study.client}` : ''}.
        </Typography>
        <Button
          component="a"
          href={resultsHref}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          size="large"
          startIcon={<InsightsOutlinedIcon />}
          sx={{ borderRadius: 34, px: 3.5, py: 1.25, fontWeight: 700 }}
        >
          Zobacz swoje wyniki
        </Button>
      </Stack>
    </Box>
  )
}

// ──────────────────────────── Screen 7: Your results ───────────────────────

/** Reuse the researcher player's stimulus shape from the respondent stimulus. */
function toStageStimulus(study: RespondentStudy, stimulusId: number) {
  const s = study.stimuli.find((x) => x.id === stimulusId)
  if (!s) return null
  return {
    id: s.id,
    order: s.order,
    title: s.title,
    type: s.type,
    url: s.url,
    payload: s.payload,
    exposure_ms: s.exposure_ms,
  }
}

function ResultsScreen({
  study,
  token,
  sessionToken,
  onBack,
}: {
  study: RespondentStudy
  token: string
  sessionToken: string
  /** Omitted on the standalone results page (its own tab/URL), where there is
   *  no in-flow screen to go back to. */
  onBack?: () => void
}) {
  const [data, setData] = useState<RespondentResults | null>(null)
  const [networkError, setNetworkError] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)

  // Playback clock. A real <video> stimulus owns it (reported via onTick);
  // otherwise a rAF wall-clock below drives it.
  const [currentMs, setCurrentMs] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [videoFailed, setVideoFailed] = useState(false)

  // Poll the results endpoint until analysis is no longer processing.
  useEffect(() => {
    let cancelled = false
    let timer: number | undefined
    // The webcam upload may still be in flight when results first open, in which
    // case the backend briefly reports `no_recording`. Keep the spinner up and
    // retry a few times before accepting it as final, so a slow upload doesn't
    // strand the respondent on "Brak nagrania".
    let noRecordingTries = 0
    const MAX_NO_RECORDING_TRIES = 6 // ~24s at the 4s cadence
    const poll = async () => {
      try {
        const res = await respondentService.getResults(token, sessionToken)
        if (cancelled) return
        setNetworkError(false)
        if (res.status === 'no_recording' && noRecordingTries < MAX_NO_RECORDING_TRIES) {
          noRecordingTries += 1
          timer = window.setTimeout(poll, 4000) // keep loading; don't commit yet
          return
        }
        setData(res)
        if (res.status === 'processing') timer = window.setTimeout(poll, 4000)
      } catch {
        if (cancelled) return
        setNetworkError(true)
        timer = window.setTimeout(poll, 6000)
      }
    }
    poll()
    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
    }
  }, [token, sessionToken])

  const results = data?.results ?? []
  const withData = useMemo(() => results.filter((r) => r.has_data), [results])

  // Default to the first stimulus that has an emotion curve.
  useEffect(() => {
    if (selectedId == null && withData.length) setSelectedId(withData[0].stimulus_id)
  }, [withData, selectedId])

  const selected: RespondentStimulusResult | null =
    results.find((r) => r.stimulus_id === selectedId) ?? withData[0] ?? null
  const durationMs = selected?.duration_ms ?? 0
  const stageStimulus = selected ? toStageStimulus(study, selected.stimulus_id) : null

  // The respondent's own face capture, streamed straight from the public
  // endpoint (range-enabled) so it can be scrubbed in sync with the stimulus.
  const faceSrc = useMemo(
    () => respondentService.recordingUrl(token, sessionToken),
    [token, sessionToken],
  )

  // Reset the playhead when the active stimulus changes.
  useEffect(() => {
    setCurrentMs(0)
    setPlaying(false)
    setVideoFailed(false)
  }, [selectedId])

  // When the stimulus renders as a real <video>, the video element owns the
  // clock (the stage reports its time via onTick) — a wall-clock can't run
  // ahead of a buffering video without degrading playback into a seek storm.
  const videoDrivesClock = !videoFailed && stimulusDrivesClock(stageStimulus)

  const handleVideoTick = useCallback(
    (ms: number) => {
      if (durationMs > 0 && ms >= durationMs) {
        setPlaying(false)
        setCurrentMs(durationMs)
      } else {
        setCurrentMs(ms)
      }
    },
    [durationMs],
  )

  const handleVideoEnded = useCallback(() => {
    setPlaying(false)
    setCurrentMs(durationMs)
  }, [durationMs])

  // rAF clock — drives currentMs only for stimuli without a playable video
  // (image / text / placeholder).
  const rafRef = useRef<number>(0)
  useEffect(() => {
    if (!playing || durationMs <= 0 || videoDrivesClock) return
    let last = performance.now()
    const tick = (now: number) => {
      const dt = now - last
      last = now
      setCurrentMs((ms) => {
        const next = ms + dt
        if (next >= durationMs) {
          setPlaying(false)
          return durationMs
        }
        return next
      })
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [playing, durationMs, videoDrivesClock])

  const togglePlay = useCallback(() => {
    if (durationMs <= 0) return
    setPlaying((p) => {
      if (!p && currentMs >= durationMs) setCurrentMs(0)
      return !p
    })
  }, [durationMs, currentMs])

  const replay = useCallback(() => {
    setCurrentMs(0)
    setPlaying(true)
  }, [])

  const stimLabel = (r: RespondentStimulusResult) => r.title?.trim() || `Materiał ${r.order + 1}`

  // ── Loading / status states (keep the spinner while analysis runs) ──
  const status = data?.status
  const isLoading = !data || status === 'processing'

  // Compact back-button reused on the navbar-less screen (top-left on the
  // status states, and at the top of the left column on the main layout).
  const backButton = onBack ? (
    <Button onClick={onBack} startIcon={<ArrowBackIcon />} sx={{ color: C.ink72, fontWeight: 600, alignSelf: 'flex-start' }}>
      Wróć
    </Button>
  ) : null

  // Full-screen centered states (loading / no_recording / failed / no_data),
  // each with a back button so the respondent can leave without a navbar.
  const fullScreenState = (content: React.ReactNode) => (
    <Box sx={{ position: 'relative', minHeight: '100dvh', bgcolor: '#F7F7FB', display: 'grid', placeItems: 'center', p: { xs: 2.5, md: 4 } }}>
      <Box sx={{ position: 'absolute', top: { xs: 12, md: 20 }, left: { xs: 12, md: 24 } }}>{backButton}</Box>
      {content}
    </Box>
  )

  if (isLoading) {
    return fullScreenState(
      <Stack spacing={2.5} alignItems="center" sx={{ textAlign: 'center', maxWidth: 460, py: 8 }}>
        <CircularProgress sx={{ color: C.coral }} />
        <Typography variant="h5" sx={{ fontSize: { xs: 22, md: 26 } }}>
          Analizujemy Twoje emocje…
        </Typography>
        <Typography sx={{ color: C.ink56, fontSize: 15, lineHeight: 1.6 }}>
          To może potrwać chwilę. Możesz poczekać na tej stronie — wyniki pojawią się automatycznie.
        </Typography>
        {networkError && (
          <Typography sx={{ color: C.ink48, fontSize: 12.5 }}>
            Ponawiamy próbę połączenia…
          </Typography>
        )}
      </Stack>,
    )
  }
  if (status === 'no_recording') {
    return fullScreenState(
      <StatusMessage
        title="Brak nagrania"
        body="Nie zarejestrowaliśmy nagrania dla tej sesji, więc nie możemy pokazać analizy emocji."
      />,
    )
  }
  if (status === 'failed') {
    return fullScreenState(
      <StatusMessage
        title="Nie udało się przygotować wyników"
        body="Podczas analizy wystąpił błąd. Twoje odpowiedzi zostały zapisane."
      />,
    )
  }
  if (!withData.length || !selected) {
    return fullScreenState(
      <StatusMessage
        title="Brak danych o emocjach"
        body="Nie wykryliśmy wystarczająco twarzy w nagraniu, aby pokazać przebieg emocji."
      />,
    )
  }

  // Stimulus picker chips — horizontal-wrapping on mobile, stacked on desktop.
  const pickerChips =
    withData.length > 1 ? (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', flexDirection: { xs: 'row', md: 'column' }, alignItems: { md: 'flex-start' }, gap: 1 }}>
        {withData.map((r) => {
          const active = r.stimulus_id === selected.stimulus_id
          return (
            <Chip
              key={r.stimulus_id}
              label={stimLabel(r)}
              onClick={() => setSelectedId(r.stimulus_id)}
              variant={active ? 'filled' : 'outlined'}
              color={active ? 'primary' : 'default'}
              sx={{ fontWeight: active ? 700 : 500, borderRadius: 2 }}
            />
          )
        })}
      </Box>
    ) : null

  // Left column: navigation + heading (and, on desktop, the stimulus picker).
  const leftColumn = (
    <Stack spacing={1.75} sx={{ width: { xs: '100%', md: 220 }, flexShrink: 0 }}>
      {backButton}
      <Box>
        <Typography sx={{ fontWeight: 800, fontSize: { xs: 24, md: 28 }, lineHeight: 1.2 }}>Twoje wyniki</Typography>
        <Typography sx={{ color: C.ink56, fontSize: 14, lineHeight: 1.5, mt: 0.75 }}>
          Przebieg Twoich emocji podczas oglądania materiału.
        </Typography>
      </Box>
      {/* Picker lives in the left column on desktop; on mobile it sits below the
          heading in the same stacked flow. */}
      {pickerChips}
    </Stack>
  )

  // Center column: the video card with transport bar + emotion timeline. The
  // video is capped so the whole column stays within the viewport height.
  const centerColumn = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: { xs: 1.5, md: 2 },
        borderRadius: 3,
        border: `1px solid ${C.ink08}`,
        bgcolor: '#fff',
      }}
    >
      <Stack spacing={1.5}>
        <Box sx={{ width: { xs: '100%', md: 'min(100%, calc(48vh * 16 / 9))' }, mx: 'auto' }}>
          {/* FaceTime-style swap: clicking the face pip enlarges it (with the
              emotion rectangle/colors) and tucks the stimulus into the corner. */}
          <SwappableStage
            stimulus={stageStimulus}
            stageCurrentMs={currentMs}
            playing={playing}
            onTick={handleVideoTick}
            onEnded={handleVideoEnded}
            onVideoFailed={() => setVideoFailed(true)}
            onTogglePlay={togglePlay}
            label={stimLabel(selected)}
            muted={false}
            faceSrc={faceSrc}
            faceCurrentMs={currentMs}
            faceOffsetMs={selected.window_start_ms}
            series={selected.series}
            gridMs={selected.grid_ms}
            faceLabels={PL_LABEL}
          />
        </Box>

        {/* Transport bar */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <IconButton onClick={togglePlay} color="primary" sx={{ bgcolor: 'rgba(234,80,72,.12)' }}>
            {playing ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
          </IconButton>
          <IconButton onClick={replay} size="small">
            <ReplayRoundedIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13, color: C.ink72 }}>
            {fmtTime(currentMs / 1000)} / {fmtTime(durationMs / 1000)}
          </Typography>
        </Stack>

        <EmotionTimeline
          series={selected.series}
          gridMs={selected.grid_ms}
          durationMs={durationMs}
          currentMs={currentMs}
          onScrub={(ms) => {
            setPlaying(false)
            setCurrentMs(Math.max(0, Math.min(ms, durationMs)))
          }}
        />
      </Stack>
    </Box>
  )

  // Right column: at-a-glance summary chart that tracks the playhead.
  const rightColumn = (
    <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
      <EmotionSummaryPanel series={selected.series} gridMs={selected.grid_ms} currentMs={currentMs} />
    </Box>
  )

  // Desktop: everything within 100dvh, three columns, vertically centered, no
  // page scroll. Mobile: stacked and scrollable.
  return (
    <Box
      sx={{
        bgcolor: '#F7F7FB',
        minHeight: { xs: '100dvh', md: 'auto' },
        height: { xs: 'auto', md: '100dvh' },
        overflow: { xs: 'visible', md: 'hidden' },
        display: 'flex',
        alignItems: { xs: 'stretch', md: 'center' },
        p: { xs: 2.5, md: 4 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1280,
          mx: 'auto',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'center' },
          gap: { xs: 2.25, md: 3 },
        }}
      >
        {leftColumn}
        {centerColumn}
        {rightColumn}
      </Box>
    </Box>
  )
}

/**
 * Standalone, refreshable results page — mounted at `/r/:token/results?s=<session>`.
 *
 * The in-flow journey keeps the session token only in React state, so a reload
 * would drop the respondent back at the start. This page reconstructs the
 * results purely from the URL (study token in the path, session token in the
 * `s` query param), letting the respondent reload, bookmark or share the page.
 */
export function RespondentResultsPage() {
  const theme = useMemo(() => createAffexyTheme(), [])
  const { token = '' } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const sessionToken = searchParams.get('s') ?? ''

  const [study, setStudy] = useState<RespondentStudy | null>(null)
  const [errorMsg, setErrorMsg] = useState<string>('')

  useEffect(() => {
    if (!token) return
    let cancelled = false
    respondentService
      .getStudy(token, sessionToken)
      .then((data) => {
        if (!cancelled) setStudy(data)
      })
      .catch((err) => {
        if (!cancelled) setErrorMsg(err instanceof Error ? err.message : 'Nie znaleziono badania')
      })
    return () => {
      cancelled = true
    }
  }, [token, sessionToken])

  let content: React.ReactNode
  if (errorMsg || !sessionToken) {
    content = (
      <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', bgcolor: '#fff', p: 3 }}>
        <Stack spacing={1.5} alignItems="center" sx={{ textAlign: 'center', maxWidth: 420 }}>
          <Lockup />
          <Typography variant="h5" sx={{ mt: 2 }}>
            Nie można otworzyć wyników
          </Typography>
          <Typography sx={{ color: C.ink56 }}>
            {errorMsg || 'Link do wyników jest nieprawidłowy lub wygasł.'}
          </Typography>
        </Stack>
      </Box>
    )
  } else if (!study) {
    content = (
      <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', bgcolor: '#fff' }}>
        <CircularProgress sx={{ color: C.coral }} />
      </Box>
    )
  } else {
    content = <ResultsScreen study={study} token={token} sessionToken={sessionToken} />
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {content}
    </ThemeProvider>
  )
}

function StatusMessage({ title, body }: { title: string; body: string }) {
  return (
    <Stack spacing={1.5} alignItems="center" sx={{ textAlign: 'center', maxWidth: 460, py: 8 }}>
      <Typography variant="h5" sx={{ fontSize: { xs: 22, md: 26 } }}>
        {title}
      </Typography>
      <Typography sx={{ color: C.ink56, fontSize: 15, lineHeight: 1.6 }}>{body}</Typography>
    </Stack>
  )
}
